import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Stack,
  Tooltip,
  Typography
} from '@mui/material'
import React, { useMemo } from 'react'
import { Link as RouterLink } from 'react-router-dom'

import {
  getBackgroundColorFromReliabilityScore,
  getServiceNotInTeamTooltipMessage,
  SERVICE_CARD_LINE_CHART_HEIGHT,
  SERVICE_CARD_LINE_CHART_WIDTH
} from 'components/reliability-management/company-dashboard/rm-company-dashboard-utils'
import { RMLineChart } from 'components/reports/RMLineChart'
import { formatOwnerDetails } from 'components/services/service-utils'
import useAuth from 'hooks/use-auth'
import { useGetService } from 'hooks/use-services'
import { muiTheme } from 'mui-theme'
import { ServiceReportResponse } from 'types/api/policies/reliability-report'
import { formatDateTime } from 'utils/formatDate'

export const DashboardServiceListCard: React.FC<{
  service: ServiceReportResponse
}> = ({ service }) => {
  const { currentTeam, currentTeamId } = useAuth()

  const { lastModified, score, serviceName } = service
  const { teamName, teamOwnerName, teamOwnerEmail } = service.teamOwner
  const userDefinedService = useGetService(service.serviceId)?.data

  const { identifier, firstName, lastName } =
    userDefinedService?.serviceOwner || {
      identifier: undefined,
      firstName: undefined,
      lastName: undefined
    }

  const isInCurrentTeam = userDefinedService?.teamId === currentTeamId

  const tooltipMessage =
    !isInCurrentTeam && getServiceNotInTeamTooltipMessage(currentTeam.name)

  const chartData = useMemo(() => {
    if (service?.reliabilityReportTrend?.statsByDate) {
      return service.reliabilityReportTrend.statsByDate
        .map((data) => ({
          date: new Date(data.trackingDate),
          score: data.score
        }))
        .reverse()
    }

    return []
  }, [service.reliabilityReportTrend.statsByDate])

  return (
    <Tooltip arrow placement="top" title={tooltipMessage}>
      <Card
        data-test-company-dashboard-service-list-card
        sx={(muiTheme) => ({
          backgroundColor: getBackgroundColorFromReliabilityScore(
            muiTheme,
            score
          )
        })}
      >
        <CardActionArea
          component={RouterLink}
          data-test-company-dashboard-service-list-card-action={
            service.serviceId
          }
          disabled={!isInCurrentTeam}
          to={`services/${service.serviceId}`}
        >
          <CardContent
            sx={{
              alignItems: 'center',
              display: 'flex',
              paddingRight: 8
            }}
          >
            <Stack flexGrow={2}>
              <Typography data-test-service-name mb={1} variant="h6">
                {serviceName}
              </Typography>
              <Typography data-test-last-modified>
                Last modified: {formatDateTime(lastModified)}
              </Typography>
              {userDefinedService?.serviceOwner ? (
                <Typography data-test-team-information>
                  {`${formatOwnerDetails(
                    identifier,
                    firstName,
                    lastName
                  )} in ${teamName}`}
                </Typography>
              ) : (
                <Typography data-test-team-information>
                  {`${teamOwnerName} (${teamOwnerEmail}) in ${teamName}`}
                </Typography>
              )}
            </Stack>
            <Stack
              direction="row"
              flexGrow={1}
              gap={3}
              justifyContent="flex-end"
            >
              <Box
                sx={{
                  height: SERVICE_CARD_LINE_CHART_HEIGHT,
                  width: SERVICE_CARD_LINE_CHART_WIDTH
                }}
              >
                {chartData.length > 0 && (
                  <RMLineChart
                    css={{
                      backgroundColor: 'transparent',
                      margin: 0
                    }}
                    data={chartData}
                    dataSeries={['score']}
                    domain={{ y: [0, 100] }}
                    height={SERVICE_CARD_LINE_CHART_HEIGHT}
                    lineColor={muiTheme.palette.grey[700]}
                    numOfTicksXAxis={0} // hides the tick labels
                    numOfTicksYAxis={0}
                    showGridLines={false}
                    showYAxis={false}
                    width={SERVICE_CARD_LINE_CHART_WIDTH}
                    xAxisLabel="Last 30 days"
                    xAxisLabelOffset={-10}
                    xKey="date"
                    yKey="score"
                  />
                )}
              </Box>
              <Typography
                data-test-score
                sx={{ marginY: 'auto', width: 20 }} // preserves line chart and score alignment
                variant="h4"
              >
                {score}
              </Typography>
            </Stack>
          </CardContent>
        </CardActionArea>
      </Card>
    </Tooltip>
  )
}
